import type {
    ChangeSet, ChangeSetItem, ChangeSetRow, ChangeSetStatus, DecisionItem, ItemCollection, Severity,
} from '../../../api/ontologyChangeSetApi';

/**
 * The rules of reviewing a change set, kept out of the components so they can be tested
 * on their own. Spec: §4.4, §5.2 and §5.5 of docs/policy-document-ontology-ingestion-api-ui.md.
 */

export type Choice = 'accept' | 'reject' | 'edit';
export type Choices = Record<string, Choice | undefined>;
export type Edits = Record<string, Record<string, unknown>>;
export type Notes = Record<string, string>;

// ─── Vocabulary ──────────────────────────────────────────────────────────────

/** The order the engine writes items in, and so the order they are reviewed in. */
export const COLLECTION_ORDER: ItemCollection[] = ['source_document', 'concept', 'policy', 'parameter'];

export const COLLECTION_LABEL: Record<ItemCollection, string> = {
    source_document: 'Source document',
    concept: 'Concepts',
    policy: 'Policies',
    parameter: 'Thresholds',
};

export const STATUS_LABEL: Record<ChangeSetStatus, string> = {
    extracting: 'Reading document',
    proposed: 'Proposed',
    pending_review: 'Awaiting review',
    resolving: 'Applying',
    applied: 'Applied',
    partially_applied: 'Partially applied',
    rejected: 'Rejected',
    failed: 'Failed',
};

export const STATUS_COLOR: Record<ChangeSetStatus, string> = {
    extracting: '#94A3B8',
    proposed: '#94A3B8',
    pending_review: '#FFDE59',
    resolving: '#60A5FA',
    applied: '#4ADE80',
    partially_applied: '#FBBF24',
    rejected: '#F87171',
    failed: '#F87171',
};

export const CHANGE_TYPE_COLOR: Record<string, string> = {
    add: '#4ADE80',
    modify: '#60A5FA',
    retire: '#F87171',
    unchanged: '#64748B',
};

export const SEVERITY_COLOR: Record<Severity, string> = {
    high: '#F87171',
    medium: '#FBBF24',
    low: '#94A3B8',
};

/**
 * Fields a reviewer may edit, per collection (spec §5.2). The engine does not enforce
 * this, and drops identity fields itself; it is what keeps an edit a correction of what
 * the document says rather than a different element.
 */
export const EDITABLE_FIELDS: Record<ItemCollection, string[]> = {
    parameter: ['value', 'unit', 'operator', 'scope', 'constraint'],
    concept: ['preferredLabel', 'definition', 'guidance'],
    policy: ['title', 'narrative', 'clauses', 'ownerRole'],
    source_document: [
        'title', 'versionLabel', 'issuer', 'issuanceDate', 'effectiveDate',
        'documentIdentifier', 'docClass', 'summary',
    ],
};

// ─── Time ────────────────────────────────────────────────────────────────────

/**
 * A timestamp from the engine. Values read back from MongoDB carry no offset but are UTC
 * (spec §1.3); `new Date()` would read them as local time.
 */
export const utcDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const parsed = new Date(hasZone || isDateOnly ? value : `${value}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatTimestamp = (value?: string | null): string => {
    const date = utcDate(value);
    if (!date) return '—';
    return date.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

/** "3 h ago", "2 d ago" — how long something has waited. */
export const formatAge = (value?: string | null, now: number = Date.now()): string => {
    const date = utcDate(value);
    if (!date) return '—';
    const minutes = Math.max(0, Math.round((now - date.getTime()) / 60000));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours} h ago`;
    return `${Math.round(hours / 24)} d ago`;
};

// ─── Queue helpers ───────────────────────────────────────────────────────────

/** "12 added · 3 changed", leaving out unchanged. */
export const changeCountsLabel = (summary?: ChangeSetRow['summary'] | null): string => {
    const counts = summary?.byChangeType ?? {};
    const parts = [
        counts.add ? `${counts.add} added` : '',
        counts.modify ? `${counts.modify} changed` : '',
        counts.retire ? `${counts.retire} retired` : '',
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'No changes';
};

export const worstSeverity = (bySeverity?: Partial<Record<Severity, number>> | null): Severity | null => {
    for (const severity of ['high', 'medium', 'low'] as Severity[]) {
        if ((bySeverity?.[severity] ?? 0) > 0) return severity;
    }
    return null;
};

/** Other change sets awaiting review for the same document — competing versions (spec §7). */
export const competingChangeSets = (row: ChangeSetRow, rows: ChangeSetRow[]): ChangeSetRow[] =>
    row.sourceDocCode
        ? rows.filter((other) => other.changeSetId !== row.changeSetId
            && other.sourceDocCode === row.sourceDocCode
            && other.status === 'pending_review')
        : [];

// ─── Items ───────────────────────────────────────────────────────────────────

export const itemLabel = (item: ChangeSetItem): string => {
    const proposed = item.proposed ?? {};
    return proposed.preferredLabel || proposed.title || item.code;
};

/** Items that need a decision: unchanged ones are ignored by the engine. */
export const reviewableItems = (items: ChangeSetItem[] = []): ChangeSetItem[] =>
    items.filter((item) => item.changeType !== 'unchanged');

export const groupByCollection = (items: ChangeSetItem[]): Array<[ItemCollection, ChangeSetItem[]]> =>
    COLLECTION_ORDER
        .map((collection): [ItemCollection, ChangeSetItem[]] => [collection, items.filter((item) => item.collection === collection)])
        .filter(([, group]) => group.length > 0);

export const validationErrors = (item: ChangeSetItem) =>
    (item.validation ?? []).filter((entry) => entry.severity === 'error');

export const validationWarnings = (item: ChangeSetItem) =>
    (item.validation ?? []).filter((entry) => entry.severity === 'warning');

/** An item with a validation error cannot be accepted as proposed (spec §5.2). */
export const canAccept = (item: ChangeSetItem): boolean => validationErrors(item).length === 0;

/** Edit needs a proposed document to merge over; a retire has none. */
export const canEdit = (item: ChangeSetItem): boolean =>
    item.changeType !== 'retire' && item.proposed !== null && typeof item.proposed === 'object';

/**
 * The starting choice. The engine's suggestion pre-ticks Accept; anything it is unsure of
 * is left for the reviewer to choose explicitly. An item that cannot be accepted is never
 * pre-ticked, whatever the suggestion.
 */
export const initialChoices = (items: ChangeSetItem[] = []): Choices => {
    const choices: Choices = {};
    for (const item of reviewableItems(items)) {
        choices[item.itemId] = item.suggestedDecision === 'accepted' && canAccept(item) ? 'accept' : undefined;
    }
    return choices;
};

/**
 * Items the engine will reject because something they depend on is rejected — directly or
 * through a chain. Maps each to the rejected items that cause it.
 */
export const cascadeRejected = (items: ChangeSetItem[], choices: Choices): Map<string, string[]> => {
    const rejected = new Set(
        reviewableItems(items).filter((item) => choices[item.itemId] === 'reject').map((item) => item.itemId),
    );
    const cascaded = new Map<string, string[]>();

    let changed = true;
    while (changed) {
        changed = false;
        for (const item of reviewableItems(items)) {
            if (rejected.has(item.itemId) || cascaded.has(item.itemId)) continue;
            const causes = (item.dependsOn ?? []).filter((dep) => rejected.has(dep) || cascaded.has(dep));
            if (causes.length) {
                cascaded.set(item.itemId, causes);
                changed = true;
            }
        }
    }
    return cascaded;
};

/** What will actually happen to the item once submitted. */
export const effectiveChoice = (
    item: ChangeSetItem, choices: Choices, cascaded: Map<string, string[]>,
): Choice | undefined => (cascaded.has(item.itemId) ? 'reject' : choices[item.itemId]);

const hasEdits = (edits?: Record<string, unknown>) => Boolean(edits) && Object.keys(edits as object).length > 0;

// ─── Editing ─────────────────────────────────────────────────────────────────

const stable = (value: unknown) => JSON.stringify(value ?? null);

/** Whether an edited field differs from what is proposed. */
export const differsFromProposed = (item: ChangeSetItem, field: string, value: unknown): boolean =>
    stable((item.proposed ?? {})[field]) !== stable(value);

/**
 * Read a typed value back from an edit field, in the type the proposed value has. Objects
 * and arrays are edited as JSON and REPLACE the proposed value whole (spec §5.2), so they
 * must parse.
 */
export const parseEditValue = (original: unknown, raw: string): { value?: unknown; error?: string } => {
    if (typeof original === 'number') {
        const trimmed = raw.trim();
        const parsed = Number(trimmed);
        return trimmed !== '' && Number.isFinite(parsed) ? { value: parsed } : { error: 'Must be a number' };
    }
    if (typeof original === 'boolean') {
        if (raw === 'true' || raw === 'false') return { value: raw === 'true' };
        return { error: 'Must be true or false' };
    }
    if (original !== null && typeof original === 'object') {
        try {
            return { value: JSON.parse(raw) };
        } catch {
            return { error: 'Must be valid JSON' };
        }
    }
    return { value: raw };
};

/** How a proposed value is shown in an edit field. */
export const editText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

// ─── Submitting ──────────────────────────────────────────────────────────────

/**
 * Whether the change set writes regulatory-tier elements, which only its configured
 * regulatory approvers may decide (spec §5.4).
 */
export const writesRegulatoryTier = (changeSet: Pick<ChangeSet, 'scopeLevel' | 'items'>): boolean =>
    changeSet.scopeLevel === 'regulatory'
    || (changeSet.items ?? []).some((item) =>
        item.proposed?.scopeLevel === 'regulatory'
        || (item.changeType === 'retire' && item.incumbent?.scopeLevel === 'regulatory'));

export const canUserDecide = (changeSet: ChangeSet, userEmail?: string | null): boolean => {
    if (!writesRegulatoryTier(changeSet)) return true;
    const approvers = (changeSet.regulatoryApprovers ?? []).map((email) => email.toLowerCase());
    return Boolean(userEmail) && approvers.includes(String(userEmail).toLowerCase());
};

/**
 * Why Submit is disabled, in the reviewer's words (spec §5.5). Empty when it can go.
 */
export const submitBlockers = (
    changeSet: ChangeSet, choices: Choices, edits: Edits, userEmail?: string | null,
): string[] => {
    const blockers: string[] = [];
    const items = reviewableItems(changeSet.items);

    if (changeSet.status !== 'pending_review') {
        blockers.push(`This change set is not awaiting review (it is ${STATUS_LABEL[changeSet.status] ?? changeSet.status}).`);
    }
    if (!canUserDecide(changeSet, userEmail)) {
        blockers.push(`Only a regulatory approver can decide this change set: ${(changeSet.regulatoryApprovers ?? []).join(', ') || 'none is configured'}.`);
    }

    const undecided = items.filter((item) => !choices[item.itemId]);
    if (undecided.length) {
        blockers.push(`${undecided.length} item${undecided.length === 1 ? '' : 's'} still need${undecided.length === 1 ? 's' : ''} a decision.`);
    }

    for (const item of items) {
        const choice = choices[item.itemId];
        if (choice === 'accept' && !canAccept(item)) {
            blockers.push(`${itemLabel(item)} has a validation error and cannot be accepted as proposed. Edit or reject it.`);
        }
        if (choice === 'edit' && !hasEdits(edits[item.itemId])) {
            blockers.push(`The edit to ${itemLabel(item)} changes nothing. Change a field, or choose Accept.`);
        }
    }

    // Every element cites the source document, but the engine does not cascade a rejection
    // from it: accepting anything without it leaves citations to an unregistered document.
    const cascaded = cascadeRejected(changeSet.items ?? [], choices);
    const sourceDocument = items.find((item) => item.collection === 'source_document');
    const anythingElseKept = items.some((item) => item.collection !== 'source_document'
        && ['accept', 'edit'].includes(effectiveChoice(item, choices, cascaded) ?? ''));
    if (sourceDocument && anythingElseKept && !['accept', 'edit'].includes(choices[sourceDocument.itemId] ?? '')) {
        blockers.push('Accept the source document too: everything else you accept cites it.');
    }

    return blockers;
};

/**
 * The `items` of the decision. Every reviewable item is listed explicitly — the engine
 * rejects anything left out — so the request says exactly what the reviewer saw.
 */
export const buildDecisionItems = (
    items: ChangeSetItem[] = [], choices: Choices, edits: Edits, notes: Notes,
): DecisionItem[] =>
    reviewableItems(items).map((item) => {
        const choice = choices[item.itemId] ?? 'reject';
        const decision: DecisionItem = { itemId: item.itemId, decision: choice };
        if (choice === 'edit') {
            const allowed = new Set(EDITABLE_FIELDS[item.collection] ?? []);
            decision.edits = Object.fromEntries(
                Object.entries(edits[item.itemId] ?? {}).filter(([field]) => allowed.has(field)),
            );
        }
        const note = notes[item.itemId]?.trim();
        if (note) decision.note = note;
        return decision;
    });

/** What the confirmation dialog lists. */
export const decisionSummary = (items: ChangeSetItem[] = [], choices: Choices) => {
    const cascaded = cascadeRejected(items, choices);
    const reviewable = reviewableItems(items);
    const byChoice = (choice: Choice) => reviewable.filter((item) =>
        !cascaded.has(item.itemId) && choices[item.itemId] === choice);
    return {
        accepted: byChoice('accept'),
        edited: byChoice('edit'),
        rejected: byChoice('reject'),
        cascaded: reviewable.filter((item) => cascaded.has(item.itemId)),
    };
};

// ─── Outcome ─────────────────────────────────────────────────────────────────

export const isDecided = (status?: ChangeSetStatus) =>
    status === 'applied' || status === 'partially_applied' || status === 'rejected';

/**
 * When a written element takes effect: the date stated beside it, else the document's,
 * if that is still in the future; otherwise immediately (spec §5.6).
 */
export const takesEffectOn = (item: ChangeSetItem, changeSet: ChangeSet, now: number = Date.now()): string | null => {
    const stated = item.statedEffectiveFrom || changeSet.documentEffectiveFrom;
    const date = utcDate(stated);
    if (!stated || !date || date.getTime() <= now) return null;
    return stated;
};
